import { geometryUtils } from '../geometryUtils';

import { clip } from './engine';
import type { ClipPolygon as PcPolygon } from './engine';
import {
	boundingBox,
	bboxOverlaps,
	countDistinctPoints,
	fromPcRing,
	groupRings,
	toPcPolygon,
	toPcRing,
} from './geometry';
import type {
	BBox,
	CutterReason,
	CutterResult,
	Ring,
	RingSet,
	SubtractBase,
	SubtractResult,
} from './types';

/** Clipping openings for rendering, and subtracting whole layers from one another. */

export const clipHolesToRing = (outer: Ring, holes: Ring[]): Ring[] => {
	if (holes.length === 0) return [];

	const outerPolygon = [toPcRing(outer)] as PcPolygon;
	const clipped: Ring[] = [];

	for (const hole of holes) {
		if (!geometryUtils.isPathClosed(hole)) continue;

		// A failed clip drops this opening only: the shape still renders, without
		// its hole, instead of the whole canvas failing to draw.
		const clippedHole = clip.intersection([toPcRing(hole)], outerPolygon);
		if (clippedHole === null) continue;

		for (const polygon of clippedHole) {
			const ring = polygon[0];
			if (!ring) continue;

			const converted = fromPcRing(ring);
			if (converted) clipped.push(converted);
		}
	}

	return clipped;
};

export const classifyCutter = (cutter: Ring): CutterReason | undefined => {
	if (!geometryUtils.isPathClosed(cutter)) return 'not-closed';
	if (countDistinctPoints(cutter) < 3) return 'degenerate';
	return undefined;
};

/**
 * Subtract `cutters` from `base`.
 *
 * Applied PER OUTER RING, deliberately. `layer.paths` is not guaranteed disjoint
 * and measurement sums per-ring areas, so overlapping outer rings double-count
 * their overlap today. Clipping the layer as a single multipolygon would make the
 * library normalise that away and silently change the layer's area in regions the
 * user never cut. Per-ring is equivalent for disjoint geometry (difference
 * distributes over disjoint union) and preserves today's numbers otherwise.
 *
 * Within a ring, every intersecting cutter goes into ONE `difference` call so the
 * library unions them internally. Subtracting cutters one at a time and summing
 * what each removed would double-subtract wherever two cutters overlap.
 */
export const subtractRegions = (base: SubtractBase, cutters: Ring[]): SubtractResult => {
	const results: CutterResult[] = cutters.map((cutter) => {
		const reason = classifyCutter(cutter);
		return reason ? { consumed: false, reason } : { consumed: false, reason: 'no-overlap' };
	});

	const eligible = cutters
		.map((cutter, index) => ({ cutter, index }))
		.filter(({ index }) => results[index]?.reason === 'no-overlap');

	const cutterBoxes = new Map<number, BBox>(
		eligible.map(({ cutter, index }) => [index, boundingBox(cutter)])
	);

	const sets = groupRings(base.paths, base.cutouts);
	const out: RingSet[] = [];

	for (const set of sets) {
		const outerBox = boundingBox(set.outer);
		const subject = toPcPolygon(set);
		const clips: PcPolygon[] = [];
		const contributing: number[] = [];

		for (const { cutter, index } of eligible) {
			const cutterBox = cutterBoxes.get(index);
			if (!cutterBox || !bboxOverlaps(outerBox, cutterBox)) continue;

			// A cutter landing wholly inside an existing opening intersects nothing
			// and must not count as having cut. A failed probe is treated the same
			// way — it removes nothing.
			const overlap = clip.intersection(subject, [toPcRing(cutter)]);
			if (overlap === null || overlap.length === 0) continue;

			clips.push([toPcRing(cutter)]);
			contributing.push(index);
		}

		if (clips.length === 0) {
			// Untouched: preserve the ring set as-is, object identity included.
			out.push(set);
			continue;
		}

		const cut = clip.difference(subject, ...clips);

		if (cut === null) {
			// Keep the ring set whole: nothing is removed, so the layer measures
			// larger and over-orders rather than silently losing area.
			//
			// ! Consumption is recorded only BELOW, once the cut has actually
			// landed. Marking it before would let the caller's keep-or-delete rule
			// delete a subtrahend layer whose cut never happened — losing the user's
			// geometry to a library error.
			out.push(set);
			continue;
		}

		// The cut landed, so these cutters really did remove area.
		for (const index of contributing) {
			const result = results[index];
			if (result) {
				result.consumed = true;
				delete result.reason;
			}
		}

		for (const polygon of cut) {
			const outerRing = polygon[0];
			if (!outerRing) continue;

			const outer = fromPcRing(outerRing);
			if (!outer) continue;

			const holes = polygon
				.slice(1)
				.map((hole) => fromPcRing(hole))
				.filter((hole): hole is Ring => hole !== null);

			out.push({ outer, holes });
		}
	}

	return { sets: out.length === 0 ? null : out, results };
};
